import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, ChevronRight, AlertTriangle, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";
import { callAIJson } from "@/lib/ai";

interface AnalysisResult {
  score: number;
  spam_triggers: string[];
  good_signals: string[];
  recommendations: string[];
}

function safeArr<T>(v: unknown): T[] { return Array.isArray(v) ? (v as T[]) : []; }

function riskFor(score: number) {
  if (score <= 30) return { ring: "border-teal", bg: "bg-teal/10", text: "text-teal", bar: "bg-teal", label: "Low risk", headline: "Looking good — low spam risk", summary: "This email has a strong chance of landing in the inbox. Review any remaining suggestions below for even better deliverability." };
  if (score <= 60) return { ring: "border-amber-400", bg: "bg-amber-50", text: "text-amber-500", bar: "bg-amber-400", label: "Medium risk", headline: "Some spam signals detected", summary: "This email may be flagged by aggressive spam filters. Address the issues below before sending to cold lists." };
  return { ring: "border-red-400", bg: "bg-red-50", text: "text-red-500", bar: "bg-red-400", label: "High risk", headline: "High spam risk — needs fixes", summary: "This email is likely to be filtered as spam. Resolve the issues below before sending to any list." };
}

export default function SpamCheckerToolPage() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState("");
  const resultsRef = useRef<HTMLDivElement>(null);
  const charCount = subject.length + body.length;
  const [showGate, setShowGate] = useState(false);
  const [pendingResult, setPendingResult] = useState<AnalysisResult | null>(null);

  async function analyze() {
    if (!subject.trim() && !body.trim()) { setError("Please enter a subject line or email body."); return; }
    setError(""); setLoading(true); setResult(null);
    const prompt = `You are an expert email deliverability analyst. Analyze this email for spam risk.\nSubject: ${subject || "(none provided)"}\nBody:\n${body || "(none provided)"}\n\nReturn ONLY valid JSON — no markdown:\n{"score":<integer 0-100>,"spam_triggers":[<up to 6 triggers, each under 12 words>],"good_signals":[<up to 4 positive signals>],"recommendations":[<up to 5 fixes, each 1-2 sentences>]}`;
    try {
      const p = await callAIJson<Partial<AnalysisResult>>({ prompt, maxTokens: 1000 });
      const parsed = { score: Math.max(0, Math.min(100, parseInt(String(p.score ?? 0)) || 0)), spam_triggers: safeArr<string>(p.spam_triggers).slice(0, 6), good_signals: safeArr<string>(p.good_signals).slice(0, 4), recommendations: safeArr<string>(p.recommendations).slice(0, 5) };
      setPendingResult(parsed);
      setShowGate(true);
    } catch (err: unknown) {
      setError("Analysis failed: " + (err instanceof Error ? err.message : "Unknown error. Please try again."));
    } finally { setLoading(false); }
  }

  function handleGateComplete() {
    setShowGate(false);
    if (pendingResult) {
      setResult(pendingResult);
      setPendingResult(null);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }

  const risk = result ? riskFor(result.score) : null;

  return (
    <>
      {showGate && (
        <EmailGateModal toolName="spam analysis" source="/spam-checker"
          onComplete={handleGateComplete} onClose={() => { setShowGate(false); handleGateComplete(); }} />
      )}
      <SeoHead title="Free Spam Score Checker — MoreMail | Test Before You Send" description="Paste your subject line and email body to get an instant AI spam score, trigger word analysis, and actionable fixes. Free, no signup required." url={`${SITE_URL}/spam-checker`} breadcrumbs={[{ name: "Home", url: SITE_URL }, { name: "Spam Score Checker", url: `${SITE_URL}/spam-checker` }]} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate">
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-ink">Spam Score Checker</span>
        </div>
      </div>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 text-center">
        <div className="inline-flex items-center px-3 py-1.5 bg-coral/10 border border-coral/20 rounded-full text-coral text-xs font-bold mb-4">Free Tool — No signup required</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-3">Is your email going to{" "}<em className="text-coral not-italic">spam?</em></h1>
        <p className="text-slate text-sm sm:text-base max-w-xl mx-auto leading-relaxed">Paste your subject and body. Get an instant AI spam score, trigger word flags, and fixes — in seconds.</p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-white rounded-2xl border border-black/[0.08] overflow-hidden shadow-sm">
          <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-4 border-b border-black/[0.06]">
            <label htmlFor="spam-subject" className="block text-xs font-bold uppercase tracking-widest text-slate mb-2">Subject line</label>
            <input id="spam-subject" type="text" value={subject} onChange={(e) => setSubject(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !loading) analyze(); }} placeholder="e.g. Quick question about your Q4 roadmap" maxLength={200} className="w-full bg-paper rounded-xl border border-black/[0.08] px-3 sm:px-4 py-2.5 text-sm text-ink placeholder-slate/40 focus:outline-none focus:border-coral/40 focus:ring-2 focus:ring-coral/10 transition" />
          </div>
          <div className="px-4 sm:px-5 pt-4 pb-4 border-b border-black/[0.06]">
            <label htmlFor="spam-body" className="block text-xs font-bold uppercase tracking-widest text-slate mb-2">Email body</label>
            <textarea id="spam-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Paste your full email body here..." rows={7} className="w-full bg-paper rounded-xl border border-black/[0.08] px-3 sm:px-4 py-2.5 text-sm text-ink placeholder-slate/40 focus:outline-none focus:border-coral/40 focus:ring-2 focus:ring-coral/10 transition resize-y font-mono leading-relaxed" />
          </div>
          <div className="px-4 sm:px-5 py-3.5 bg-paper flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-slate/60 font-mono tabular-nums">{charCount.toLocaleString()} character{charCount !== 1 ? "s" : ""}</span>
            <button type="button" onClick={analyze} disabled={loading} className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-coral text-white text-sm font-semibold rounded-full hover:bg-coral-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md shadow-coral/20 whitespace-nowrap">
              {loading ? <><Loader2 size={14} className="animate-spin flex-shrink-0" />Analyzing...</> : <>Analyze for spam <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
        {error && <div role="alert" className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
      </section>

      {result && risk && (
        <section ref={resultsRef} className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5 mb-5 sm:mb-6">
            <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 flex-shrink-0 flex flex-col items-center justify-center ${risk.ring} ${risk.bg}`}>
              <span className={`font-display text-3xl font-bold leading-none ${risk.text}`}>{result.score}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wide mt-1 ${risk.text}`}>{risk.label}</span>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left w-full">
              <h2 className="font-display text-lg sm:text-xl font-bold text-ink mb-1">{risk.headline}</h2>
              <p className="text-sm text-slate leading-relaxed">{risk.summary}</p>
              <div className="mt-3 h-2 rounded-full bg-sand overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${risk.bar}`} style={{ width: `${result.score}%` }} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />Spam triggers</p>
              {result.spam_triggers.length === 0 ? <p className="text-xs text-slate italic">No major spam triggers found.</p> : <ul className="space-y-2">{result.spam_triggers.map((t, i) => (<li key={i} className="flex items-start gap-2 text-sm"><XCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" /><span className="text-ink leading-snug">{t}</span></li>))}</ul>}
            </div>
            <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" />What's working</p>
              {result.good_signals.length === 0 ? <p className="text-xs text-slate italic">No positive signals detected yet.</p> : <ul className="space-y-2">{result.good_signals.map((g, i) => (<li key={i} className="flex items-start gap-2 text-sm"><CheckCircle size={14} className="text-teal flex-shrink-0 mt-0.5" /><span className="text-ink leading-snug">{g}</span></li>))}</ul>}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5 mb-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0" />Recommendations</p>
            {result.recommendations.length === 0 ? <p className="text-xs text-slate italic">No specific recommendations.</p> : <div className="space-y-2">{result.recommendations.map((r, i) => (<div key={i} className="flex gap-3 bg-paper rounded-xl px-3 sm:px-4 py-3 text-sm"><span className="font-bold text-coral flex-shrink-0">{i + 1}.</span><span className="text-slate leading-relaxed">{r}</span></div>))}</div>}
          </div>

          <div className="flex items-start gap-3 bg-coral/5 border border-coral/15 rounded-2xl px-4 sm:px-5 py-4 mb-5">
            <AlertTriangle size={16} className="text-coral flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate leading-relaxed"><strong className="text-ink">Want to avoid spam issues automatically?</strong>{" "}MoreMail scores every campaign before sending and blocks high-risk emails until you fix them.</p>
          </div>

          <div className="bg-ink rounded-2xl px-5 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-1">Ready to send emails that land?</h3>
              <p className="text-sm text-white/50">Auto-warmup, spam scoring, and AI campaigns — all in one place.</p>
            </div>
            <LeadCaptureBox />
          </div>
        </section>
      )}

      {!result && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
            {[{ stat: "2.4M+", label: "Emails analyzed" }, { stat: "94%", label: "Inbox placement" }, { stat: "Free", label: "No signup needed" }].map(({ stat, label }) => (
              <div key={label} className="bg-white rounded-2xl border border-black/[0.06] py-4 sm:py-5">
                <div className="font-display text-xl sm:text-2xl font-bold text-coral">{stat}</div>
                <div className="text-xs text-slate mt-1">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
